import React, { useState, useEffect, useRef } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import ListItemIcon from '@mui/material/ListItemIcon';
import { format, subDays } from 'date-fns';

const AutocompleteIssueTimestamp = ({
  cqlFechaMax,
  cqlFechaMin, // Esta es la fecha que marca el inicio del slider
  handleBackdropOpen,
  handleBackdropClose,
  handleDateSelect,
  selectedModel
}) => {
  const [issueTimeOptions, setIssueTimeOptions] = useState([]);
  const [defaultIssueTime, setDefaultIssueTime] = useState(null);
  const prevCqlFechaMin = useRef(null);

  useEffect(() => {
    handleBackdropOpen?.();

    // 🔍 DETECCIÓN DE CAPA
    const isHighResLayer = selectedModel && selectedModel.includes('euro_1k_00625_15m');

    // CASO A: Capa nueva -> Solo D-0
    // CASO B: Capas viejas -> D-0, D-1, D-2
    const daysRange = isHighResLayer ? [0] : [0, 1, 2];

    const dates = daysRange.map((daysAgo) => {
      // Calculamos la fecha basándonos en cqlFechaMin (que es el inicio del slider)
      const date = subDays(new Date(cqlFechaMin), daysAgo);
      return {
        label: `D-${daysAgo}: ${format(date, 'yyyy-MM-dd')}`,
        value: format(date, 'yyyy-MM-dd'),
      };
    });

    setIssueTimeOptions(dates);

    // Al cambiar de día en el calendario se vuelve a D-0 de ese día, aunque antes se eligiera D-1/D-2.
    // Si solo cambia la capa se mantiene el D-x elegido (mismas fechas); la capa 00625 solo tiene D-0.
    const dayChanged = prevCqlFechaMin.current !== cqlFechaMin;
    prevCqlFechaMin.current = cqlFechaMin;
    if (dates.length > 0 && (dayChanged || isHighResLayer)) {
      setDefaultIssueTime(dates[0]);
      // IMPORTANTE: Esto comunica al padre la fecha seleccionada
      handleDateSelect?.(dates[0].value);
    }

    handleBackdropClose?.();
  }, [cqlFechaMin, selectedModel]); // Dependencias simplificadas para evitar loops

  const handleManualChange = (_, newValue) => {
    if (!newValue) return;
    setDefaultIssueTime(newValue);
    handleDateSelect?.(newValue.value); // <--- ESTO DEBE ACTUALIZAR issueTime GLOBALMENTE
  };

  // ... (El resto del return visual se queda igual)
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', }}>
      <Autocomplete
        title="Click to select a different issue date"
        options={issueTimeOptions}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        value={defaultIssueTime}
        onChange={handleManualChange}
        disableClearable
        selectOnFocus={false}
        forcePopupIcon
        popupIcon={
          <>
            {/* 1. LA INICIAL: Solo en sm y md */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex', lg: 'none' },
                color: '#bbb',
                fontSize: '0.7rem',
                fontWeight: 800,
                width: 22,
                height: 22,
                alignItems: 'center',
                justifyContent: 'center',

              }}
            >
              ID
            </Box>

            {/* 2. LA FLECHA: Solo en xs y lg (usamos el SVG original) */}
            <Box
              component="span"
              sx={{
                display: { xs: 'flex', sm: 'none', lg: 'flex' },
                alignItems: 'center'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#bbb">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </Box>
          </>
        }
        slotProps={{
          popper: {
            sx: {
              width: '250px !important',
              '& .MuiPaper-root': {
                width: '200px',
                backgroundColor: 'rgba(20, 20, 20, 0.85)', // Un poco más opaco para legibilidad
                color: '#fff',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.1)',
                mb: { xs: '10px', md: '50px' },
                ml: { xs: '30px', sm: '0px', md: '40px' },

                // --- AQUÍ EL TÍTULO ---
                '&::before': {
                  content: '"SELECT ISSUE DATE"',
                  display: { xs: 'none', sm: 'block', lg: 'none' },
                  padding: '12px 16px 8px',
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  color: 'rgba(255,255,255,0.5)',
                  letterSpacing: '1px'
                }
              },
            },
          },
        }}

        ListboxProps={{
          sx: {
            width: '100%',
            py: 0.5,
            '& .MuiAutocomplete-option': {
              gap: '8px',
              whiteSpace: 'nowrap', // Evita el scroll horizontal
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' }
            },
          },
        }}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Radio edge="start" checked={selected} tabIndex={-1} disableRipple sx={{ color: '#bbb', '&.Mui-checked': { color: '#4caf50' }, cursor: 'pointer' }} />
            </ListItemIcon>
            <Typography variant="body1" sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: selected ? 700 : 500 }}>
              {option.label}
            </Typography>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Issue Date"
            variant="outlined"

            inputProps={{
              ...params.inputProps,
              readOnly: true,
              tabIndex: -1,
              onSelect: (e) => e.preventDefault(),
              style: {
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden'
              }
            }}
            onFocus={(e) => {
              if (e.target) e.target.scrollLeft = 0;
            }}
            sx={{
              width: 190,
              userSelect: 'none',
              cursor: 'pointer',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(0,0,0,0.75)',
                color: '#fff',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                backdropFilter: 'blur(2px)',
                cursor: 'pointer',
                '& fieldset': { borderColor: '#1976d2' },
                '&:hover fieldset': { borderColor: '#1565c0' },
                '&.Mui-focused fieldset': { borderColor: '#1976d2' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.9)', '&.Mui-focused': { color: '#ffffff' } },
              '& .MuiInputBase-input': {
                color: '#fff',
                py: 1.2,
                pointerEvents: 'none',
                userSelect: 'none',
                caretColor: 'transparent',
                textOverflow: 'ellipsis'
              },

              // --- AQUÍ ESTÁ EL CAMBIO ---
              '& .MuiAutocomplete-endAdornment': {
                marginRight: { xs: '0px', sm: '0px', md: '12px', lg: '0px' },
                marginLeft: '5px',
                '& .MuiAutocomplete-popupIndicator': {
                  // Evita que gire el ID o la flecha
                  transform: 'none !important',
                  '&.MuiAutocomplete-popupIndicatorOpen': {
                    transform: 'none !important'
                  }
                },
                '& .MuiSvgIcon-root': {
                  color: '#bbb',
                  cursor: 'pointer',
                  display: { xs: 'block', sm: 'none', lg: 'block' }
                }
              },
            }}
          />
        )}
        sx={{ width: 190, mt: '10px', cursor: 'pointer' }}
      />
    </Box>
  );
};

export default AutocompleteIssueTimestamp;